import ArrowSVG from '@public/icons/btn-arr.svg';
import classNames from 'classnames';
import { MutableRefObject, useRef, useState } from 'react';
import { Navigation } from 'swiper';
import { Swiper, SwiperRef, SwiperSlide } from 'swiper/react';
import { Document } from '@/entities/document';
import { SelectionStage } from '@/entities/selection-stage';
import Pagination from '@/shared/components/shared/Pagination';
import Typografed from '@/shared/components/shared/Typografed';
import { isPdf } from '@/shared/hooks/use-form-sender/validator/utils';
import { useMounted } from '@/shared/hooks/use-mounted';
import { useOverflowing } from '@/shared/hooks/use-overflowing';
import { useTypograf } from '@/shared/hooks/use-typograf';
import { Theme } from '@/shared/lib/types';
import { tp } from '@/shared/lib/typograf';
import Button from '../../shared/Button';
import Link from '../../shared/Link';
import SectionDocs from '../../shared/SectionDocs';

export type SelectionStagesSectionProps = {
    title: string;
    subtitle?: string;
    documents: Document[];
    cards: SelectionStage[];
};

type Props = React.HTMLAttributes<HTMLElement> & {
    selectionStagesSectionProps: SelectionStagesSectionProps;
    'data-theme'?: Theme;
};

export const SECTION_ID = 'selectionStages';

function SelectionStagesSection({ selectionStagesSectionProps, ...props }: Props) {
    const sliderRef = useRef<SwiperRef>();

    const [lastRealIndex, setLastRealIndex] = useState(0);
    const [currentRealIndex, setCurrentRealIndex] = useState(0);
    const numSlides = selectionStagesSectionProps.cards.length;
    const tpTitle = useTypograf(selectionStagesSectionProps.title);
    const documents = selectionStagesSectionProps.documents;

    const slider = useRef<HTMLDivElement | null>(null);
    const isInitialized = useOverflowing(slider);
    const isMounted = useMounted();

    const renderButton = (slide: SelectionStage) => {
        switch (slide.buttonType) {
            case 'form':
                return (
                    <Button
                        tag={Link}
                        href={'/registration/'}
                        variant="outline-dark"
                        className="selection-stages-card-btn"
                    >
                        Подать заявку
                    </Button>
                );
            case 'result':
                return (
                    <>
                        {slide.href && (
                            <Button
                                tag={Link}
                                href={slide.href}
                                target="_blank"
                                rel="noreferrer nofollow"
                                variant="outline-dark"
                                className="selection-stages-card-btn"
                            >
                                {slide.buttonText || 'Проверить результаты'}
                            </Button>
                        )}
                    </>
                );
            case 'file':
                return (
                    <>
                        {slide.files && slide.files.length > 0 && (
                            <a
                                href={slide.files[0].src}
                                target={isPdf(slide.files[0].src) ? '_blank' : undefined}
                                rel={isPdf(slide.files[0].src) ? 'noreferrer noopener' : undefined}
                                download={isPdf(slide.files[0].src) ? undefined : true}
                                className="btn btn-outline-dark btn-geometry-outline-dark selection-stages-card-btn"
                            >
                                <ArrowSVG className="btn__arrow"></ArrowSVG>
                                <span className="btn__text"> Скачать файл</span>
                            </a>
                        )}
                    </>
                );
            default:
                return null;
        }
    };

    return (
        numSlides > 0 && (
            <section
                {...props}
                className={classNames('selection-stages-section js-header-theme-trigger', props.className)}
                id={props.id ?? SECTION_ID}
                data-theme={props['data-theme'] ?? 'dark'}
            >
                <div className="selection-stages-top wrapper">
                    <div className="selection-stages-title h3">{tpTitle}</div>
                    {documents.length > 0 && (
                        <SectionDocs documents={documents} className="selection-stages-description-block" />
                    )}
                </div>
                {isInitialized && isMounted ? (
                    <div className="selection-stages-bottom">
                        <Swiper
                            ref={sliderRef as MutableRefObject<SwiperRef>}
                            className={classNames('selection-stages-slider', { 'is-enabled': isInitialized })}
                            modules={[Navigation]}
                            navigation={{
                                prevEl: '.services-slider-prev-btn',
                                nextEl: '.services-slider-next-btn',
                            }}
                            speed={650}
                            slidesPerView={'auto'}
                            breakpoints={{
                                320: {
                                    slidesPerView: 1.2,
                                    spaceBetween: 8,
                                    centeredSlides: true,
                                },
                                767: {
                                    spaceBetween: 12,
                                    slidesOffsetBefore: 22,
                                    slidesOffsetAfter: 22,
                                },
                                1198: {
                                    slidesOffsetBefore: 36,
                                    slidesOffsetAfter: 36,
                                },
                                1366: {
                                    spaceBetween: 20,
                                    slidesOffsetBefore: 70,
                                    slidesOffsetAfter: 70,
                                },
                            }}
                            onSlideChange={(swiper) => {
                                const newRealIndex = swiper.realIndex;
                                setLastRealIndex(currentRealIndex);
                                setCurrentRealIndex(newRealIndex);
                            }}
                        >
                            {selectionStagesSectionProps.cards.map((slide, i) => (
                                <SwiperSlide className=" selection-stages-card selection-stages-slider-slide" key={i}>
                                    <div className="selection-stages-card-text">
                                        <div
                                            className="selection-stages-card-title text-xl"
                                            dangerouslySetInnerHTML={{ __html: tp(slide.title) }}
                                        ></div>
                                        {slide.description && (
                                            <div
                                                className="selection-stages-card-description text-s wysiwyg"
                                                dangerouslySetInnerHTML={{ __html: tp(slide.description) }}
                                            ></div>
                                        )}
                                    </div>
                                    {slide.buttonType && (
                                        <div className="selection-stages-card-btn__wrapper">{renderButton(slide)}</div>
                                    )}
                                </SwiperSlide>
                            ))}
                        </Swiper>
                        <Pagination
                            numSlides={numSlides}
                            currentRealIndex={currentRealIndex}
                            lastRealIndex={lastRealIndex}
                            prevElClass="services-slider-prev-btn"
                            nextElClass="services-slider-next-btn"
                            wrapperClass="services-pagination-wrapper wrapper"
                            hidden={!isInitialized}
                        />
                    </div>
                ) : (
                    <div className="selection-stages-bottom wrapper swiper__wrapper" ref={slider}>
                        <div className="selection-stages__list ">
                            {selectionStagesSectionProps.cards.map((slide, i) => (
                                <div className="selection-stages-card selection-stages-slider-slide" key={i}>
                                    <div className="selection-stages-card-text">
                                        <div className="selection-stages-card-title text-xl">
                                            <Typografed>{slide.title}</Typografed>
                                        </div>
                                        {slide.description && (
                                            <div
                                                className="selection-stages-card-description text-s wysiwyg"
                                                dangerouslySetInnerHTML={{ __html: tp(slide.description) }}
                                            ></div>
                                        )}
                                    </div>
                                    {slide.buttonType && (
                                        <div className="selection-stages-card-btn__wrapper">{renderButton(slide)}</div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </section>
        )
    );
}

export default SelectionStagesSection;
